import ApiMeetingGql, {MeetingAdvancedJson, MeetingsUdsInput} from "../../../../gd/api/graphql/meeting"
import asyncSessionLoader from "../../../../gd/data/async-session-loader"
import DatetimeUtils from "../../../../gd/utils/datetime-utils"
import Meeting from "../../../../model2/meeting/meeting"
import EntitySelectorItem from "../../glist/item"
import {matchesSearch} from "../functions/rows"

// The window My Meetings loads (`views/my-meetings/my-meetings-controller.ts`),
// split at now: everything behind is Recent, everything ahead is Upcoming.
// `meetingsUds` requires a span -- there is no "all meetings" call.
const RECENT_MONTHS   = 6
const UPCOMING_MONTHS = 2

const debug = false
// const debug = "entitySelectorLoadMeetingItems"

export function entitySelectorLoadRecentMeetingItems(search: string): Promise<EntitySelectorItem[]> {
    return loadMeetingItems(search, moment().subtract(RECENT_MONTHS, 'months'), moment())
}

export function entitySelectorLoadUpcomingMeetingItems(search: string): Promise<EntitySelectorItem[]> {
    return loadMeetingItems(search, moment(), moment().add(UPCOMING_MONTHS, 'months'))
}

// The window is built per call, not once at module load -- a session left open
// overnight would otherwise keep asking for yesterday's range.
//
// Scoped to the viewer, like the task tab's Recent: "meetings" here means the
// ones they were in, not every meeting in the company for six months. Drop
// `userIds` to widen it.
async function loadMeetingItems(search: string, start: Moment, end: Moment): Promise<EntitySelectorItem[]> {

    const input: MeetingsUdsInput = {
        startDate   : gd.utils.momentToDate(start)!,
        endDate     : gd.utils.momentToDate(end)!,
        userIds     : [gd.session.me.id],
    }

    const rows = await ApiMeetingGql.meetingsUds(input).promise()

    // The input has no name filter, so the search is ours to apply.
    const visible = _.filter(rows, (row: MeetingAdvancedJson) => {
        return !row.isDeleted && matchesSearch(row.name, search)
    })

    debug && console.log(debug, 'loaded', input, rows.length, '->', visible.length)

    // Without this the project chips silently vanish: `GDProjectName` reads
    // `gd.session.projects`, and a meeting can sit in a project the session has
    // never loaded.
    await asyncSessionLoader(gd.session.findMissing({
        projects: _.uniq(_.compact(_.map(visible, 'projectId'))),
    }))

    // The glist's own `sortBy`/`defaultSortBy` (see `../../glist/module.ts`)
    // orders these by date -- descending for Recent, ascending for Upcoming,
    // set via `dateSortDirection` in `meeting.tsx` -- so `meetingsUds`'s own
    // order doesn't matter here.
    return _.map(visible, meetingToItem)
}

// `startMoment` is the column -- a meeting's date is when it happens, which is
// what the DATE header means here (the task and document tabs, which have no
// such moment of their own, show when the row was last touched instead).
// Creation is the fallback for a meeting with no time set.
function meetingToItem(json: MeetingAdvancedJson): EntitySelectorItem {
    const datetime = json.startMoment || json.momentCreated

    return new EntitySelectorItem({
        entity      : new Meeting(json),
        title       : json.name,
        date        : datetime ? DatetimeUtils.datetimeToMoment(datetime) : null,
        projectId   : json.projectId,
    })
}
